import type { ApiResponseBody } from "@/lib/api/api-type-helper";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";
import { queryClient } from "@/lib/queryClient";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { operationPackageQueryKeys } from "./keys";

export type GetOperationPackagesRes = ApiResponseBody<
  "/v2/contractor-core/operation-packages",
  "get"
>;

export const getOperationPackages =
  async (): Promise<GetOperationPackagesRes> => {
    const res = await apiClient.GET("/v2/contractor-core/operation-packages");
    return getDataOrThrowApiError(res);
  };

/**
 * Operation Packageの一覧を取得する
 */
export const useGetOperationPackages = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: operationPackageQueryKeys.list(),
    queryFn: () => getOperationPackages(),
  });
  return { data, isLoading, error };
};

type GetOperationPackageCountInProgressRes = ApiResponseBody<
  "/v2/contractor-core/operation-packages/{id}/count-in-progress",
  "get"
>;

/**
 * 特定のOperation Packageの実行中のInstanceの数を取得する
 */
export const getOperationPackageCountInProgress = async (
  id: string,
): Promise<GetOperationPackageCountInProgressRes> => {
  const res = await apiClient.GET(
    "/v2/contractor-core/operation-packages/{id}/count-in-progress",
    {
      params: {
        path: { id },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

export type GetOperationPackagePatternsRes = ApiResponseBody<
  "/v2/contractor-core/operation-packages/{id}/patterns",
  "get"
>;

export const getOperationPackagePatterns = async (
  id: string,
): Promise<GetOperationPackagePatternsRes> => {
  const res = await apiClient.GET(
    "/v2/contractor-core/operation-packages/{id}/patterns",
    {
      params: {
        path: { id },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

/**
 * Operation Packageのパターンの定義一覧を取得する
 */
export const useGetOperationPackagePatterns = (
  id: string,
  options?: { enabled?: boolean },
) => {
  const { data, isLoading, error } = useSuspenseQuery({
    queryKey: operationPackageQueryKeys.patterns(id),
    queryFn: () => {
      if (options?.enabled === false || !id) {
        return Promise.resolve([]);
      }
      return getOperationPackagePatterns(id);
    },
  });
  return { data, isLoading, error };
};

export type PostOperationPackagePatternsRes = ApiResponseBody<
  "/v2/contractor-core/operation-packages/{id}/patterns",
  "post"
>;

const postOperationPackagePatterns = async (
  id: string,
): Promise<PostOperationPackagePatternsRes> => {
  const res = await apiClient.POST(
    "/v2/contractor-core/operation-packages/{id}/patterns",
    {
      params: {
        path: { id },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

/**
 * 特定のOperation Packageのパターンを作成する
 */
export const usePostOperationPackagePatterns = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  },
) => {
  const { mutate, isPending, error } = useMutation({
    mutationFn: () => postOperationPackagePatterns(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operationPackageQueryKeys.patterns(id),
      });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
  return { mutate, isPending, error };
};

export type AssignTaskToPersonRes = ApiResponseBody<
  "/v2/contractor-core/operation-packages/{id}/patterns/{patternId}",
  "post"
>;

export const assignTaskToPerson = async (
  id: string,
  patternId: string,
  body: {
    targetPersonId: string;
    anchorDate: string;
  },
): Promise<AssignTaskToPersonRes> => {
  const res = await apiClient.POST(
    "/v2/contractor-core/operation-packages/{id}/patterns/{patternId}",
    {
      params: {
        path: { id, patternId },
      },
      body,
    },
  );
  return getDataOrThrowApiError(res);
};

export type GetOperationTaskCountInProgressRes = ApiResponseBody<
  "/v2/contractor-core/operation-packages/{id}/patterns/{patternId}/count-in-progress",
  "get"
>;

const getOperationTaskCountInProgress = async (
  id: string,
  patternId: string,
): Promise<GetOperationTaskCountInProgressRes> => {
  const res = await apiClient.GET(
    "/v2/contractor-core/operation-packages/{id}/patterns/{patternId}/count-in-progress",
    {
      params: {
        path: { id, patternId },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

/**
 * 特定のOperation Packageのパターンのタスクの実行中の数を取得する
 */
export const useGetOperationTaskCountInProgress = ({
  operationPackageId: id,
  patternId,
}: {
  operationPackageId: string;
  patternId: string;
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: operationPackageQueryKeys.patternCountInProgress(id, patternId),
    queryFn: () => getOperationTaskCountInProgress(id, patternId),
  });
  return { data, isLoading, error };
};

export type GetOperationPackageInstancesRes = ApiResponseBody<
  "/v2/contractor-core/operation-packages/{id}/patterns/{patternId}/instances",
  "get"
>;

const getOperationPackageInstances = async ({
  id,
  patternId,
}: {
  id: string;
  patternId: string;
}): Promise<GetOperationPackageInstancesRes> => {
  const res = await apiClient.GET(
    "/v2/contractor-core/operation-packages/{id}/patterns/{patternId}/instances",
    {
      params: {
        path: { id, patternId },
      },
    },
  );
  return getDataOrThrowApiError(res);
};

/**
 * 特定のOperation Packageのパターンのインスタンスの一覧を取得する
 */
export const useGetOperationPackageInstances = (
  params: {
    id: string;
    patternId: string;
  },
  options?: { enabled?: boolean },
) => {
  const { data, isPending, error } = useQuery({
    queryKey: operationPackageQueryKeys.instances(params.id, params.patternId),
    queryFn: () => getOperationPackageInstances(params),
    enabled: options?.enabled ?? true,
  });
  return { data, isPending, error };
};
